import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormBySchema, FormFieldLayout, Id, Item, useAPIWrapper } from 'drf-react-by-schema';

import { useAppSelector } from 'src/app/hooks';
import { appDispatch } from 'src/utils/utils';
import { setFormIsValid, setHandleSubmit, setPage } from 'src/features/pageSlice';
import { useSearchParams } from 'react-router-dom';
import { PageType } from 'src/constants/PageType';
import { Layout } from 'src/styles';

const MODEL = 'mesocarpo_processing';
const key = MODEL;

const WIZARD_OPTIONS: (entrepostoId?: Id) => {
  FIELD_LAYOUT: FormFieldLayout[];
  AUTOFOCUS_FIELD?: string;
  DEFAULTS?: Item;
}[] = (entrepostoId) => [
  {
    FIELD_LAYOUT: [
      {
        title: 'Selecionar e pesar lotes',
        rows: [
          ...(!entrepostoId ? ['entreposto'] : []),
          'data_novo_peso_flocos_seco',
          'lotes_producao',
          'novo_peso_flocos_seco',
        ],
      },
    ],
    AUTOFOCUS_FIELD: entrepostoId ? 'data_novo_peso_flocos_seco' : undefined,
    DEFAULTS: { data_novo_peso_flocos_seco: new Date() },
  },
  {
    FIELD_LAYOUT: [
      {
        title: 'Processamento',
        rows: ['data_processamento', 'peso_processado', 'peso_perda', 'peso_residuo'],
      },
    ],
    AUTOFOCUS_FIELD: undefined,
    DEFAULTS: { data_processamento: new Date() },
  },
  {
    FIELD_LAYOUT: [
      {
        title: 'Armazenamento',
        rows: ['peso_armazenado', 'observacoes', 'comentarios'],
      },
    ],
    AUTOFOCUS_FIELD: undefined,
    DEFAULTS: undefined,
  },
  {
    FIELD_LAYOUT: [
      {
        title: 'Empacotamento',
        rows: [
          'pctes_um_quarto',
          'pctes_metade',
          'data_empacotamento',
          'peso_perda_empacotamento',
          'comentarios_empacotamento',
        ],
      },
    ],
    AUTOFOCUS_FIELD: undefined,
    DEFAULTS: { data_empacotamento: new Date() },
  },
];

function objectReducer(array: Item[], key: string) {
  let reduced: Item = {};
  for (const item of array) {
    reduced = {
      ...reduced,
      ...item[key],
    };
  }
  return reduced;
}

export default function MesocarpoProcessingForm() {
  const { perfil, id, type, formWizard, formValues, isMobile } = useAppSelector(
    (state) => state.page
  );
  const { pageForm, loadSinglePageData, onEditModel } = useAPIWrapper();
  const [URLSearchParams] = useSearchParams();
  const page = parseInt(URLSearchParams.get('page') || '1');
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors, isDirty, isValid },
    register,
  } = useForm({
    mode: 'onBlur',
    resolver: pageForm?.validationSchema ? yupResolver(pageForm.validationSchema) : undefined,
  });

  const entrepostoId = useMemo(() => perfil?.entreposto?.id, [perfil?.entreposto?.id]);

  const hiddenFields = useMemo(
    () => ['id', 'status', ...(entrepostoId ? ['entreposto'] : [])],
    [entrepostoId]
  );

  const fieldsLayout = useMemo(() => {
    const allWizardOptions = WIZARD_OPTIONS(entrepostoId);
    const wizardOptions = allWizardOptions[page - 1];
    if (isMobile) {
      return wizardOptions.FIELD_LAYOUT;
    }

    const newFieldsLayout: FormFieldLayout[] = [];
    for (const item of allWizardOptions.map(({ FIELD_LAYOUT }) => FIELD_LAYOUT)) {
      newFieldsLayout.push(...item);
    }
    return newFieldsLayout;
  }, [page, isMobile, entrepostoId]);

  const autoFocusField = useMemo(() => {
    const allWizardOptions = WIZARD_OPTIONS(entrepostoId);
    const wizardOptions = allWizardOptions[page - 1];
    return isMobile ? wizardOptions.AUTOFOCUS_FIELD : allWizardOptions[0].AUTOFOCUS_FIELD;
  }, [page, isMobile, entrepostoId]);

  useEffect(() => {
    if (!fieldsLayout || !type || (type === PageType.EDIT && !id)) {
      return;
    }
    const loadAllDataAsync = async () => {
      const pageTitle = 'Produção de flocos de mesocarpo';
      const allWizardOptions = WIZARD_OPTIONS(entrepostoId);
      const wizardOptions = allWizardOptions[page - 1];

      const values = await loadSinglePageData({
        model: MODEL,
        objId: id,
        defaults: isMobile ? wizardOptions.DEFAULTS : objectReducer(allWizardOptions, 'DEFAULTS'),
        fieldsLayout,
      });

      if (values) {
        reset(values);
        if (entrepostoId) {
          setValue('entreposto', { id: entrepostoId });
        }

        appDispatch(
          setPage({
            model: MODEL,
            title: pageTitle,
            formValues: values,
          })
        );
      }
    };
    loadAllDataAsync();
  }, [id, isMobile, page, fieldsLayout, type, entrepostoId]);

  useEffect(() => {
    if (!isMobile || !type || ![PageType.EDIT, PageType.CREATE].includes(type)) {
      return;
    }
    if (!formWizard) {
      const newSubmitSuccessTarget = `/${key}/view/{ID}/?type=${type}`;
      const newFormWizard = {
        pages: WIZARD_OPTIONS().length,
        submitSuccessTarget: newSubmitSuccessTarget,
      };
      appDispatch(setPage({ formWizard: newFormWizard }));
    }
  }, [formWizard, type, id, isMobile]);

  useEffect(() => {
    appDispatch(
      setPage({
        formDisabled: !isDirty,
      })
    );
  }, [isDirty]);

  useEffect(() => {
    appDispatch(setFormIsValid(isValid));
  }, [isValid]);

  useEffect(() => {
    appDispatch(setHandleSubmit(handleSubmit));
  }, [handleSubmit]);

  if (!pageForm?.schema || !formValues || !type || !fieldsLayout) {
    return null;
  }

  return (
    <FormBySchema
      fieldsLayout={fieldsLayout}
      autoFocusField={autoFocusField}
      hiddenFields={hiddenFields}
      control={control}
      register={register}
      errors={errors}
      schema={pageForm.schema}
      onEditModel={onEditModel}
      getValues={getValues}
      setValue={setValue}
      elevation={0}
      {...Layout.formLayout}
    />
  );
}
